import React from "react";
import { Card, PageHeader } from "@/components/ui";
import {
  LayoutDashboard, Users, ClipboardList, Receipt, ShieldCheck, Send, Server, Rocket,
  FileCheck, Bot, Globe, PlayCircle, FolderDown, Inbox, Copy, Lock, HelpCircle,
} from "lucide-react";

const Section = ({ icon: Icon, title, children }) => (
  <Card className="p-6" data-testid={`help-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="flex items-center gap-2.5 mb-3">
      <span className="h-9 w-9 rounded-lg bg-brand-50 flex items-center justify-center"><Icon className="h-5 w-5 text-brand-700" /></span>
      <h2 className="font-display text-lg font-bold">{title}</h2>
    </div>
    <div className="text-sm text-muted-foreground space-y-2 leading-relaxed">{children}</div>
  </Card>
);

const Item = ({ label, children }) => (
  <p><span className="font-semibold text-foreground">{label}:</span> {children}</p>
);

export default function Help() {
  return (
    <div className="vc-fade-up max-w-5xl" data-testid="help-page">
      <PageHeader title="Help & Guide" subtitle="What every part of Go MCO does, and how to use it to win Trillium and expand to other MCOs." />

      <div className="grid grid-cols-1 gap-5">
        <Section icon={HelpCircle} title="What Go MCO is">
          <p>Go MCO is an AI-native Clinical Management Platform for Managed Care Organizations (MCOs / NC LME-MCOs). It is <strong>multi-tenant</strong>: one product serves many MCOs, and each MCO ("tenant") is configuration — its own branding, members, modules, and data, fully isolated. This app is also your <strong>business command center</strong> for responding to the Trillium RFP and selling to other MCOs.</p>
        </Section>

        <Section icon={LayoutDashboard} title="Clinical modules (the product)">
          <Item label="Dashboard">Population Health Command Center — live view of members, risk, tasks, and authorizations.</Item>
          <Item label="Members / Risk / Work Queue">Member 360 records, acuity stratification, and the prioritized action queue for care managers.</Item>
          <Item label="Assessments & Care Plans">Screening libraries and AI-assisted care-plan and clinical-summary generation (a human always approves).</Item>
          <Item label="Provider Network, Billing, UM, QM">Network adequacy, end-to-end 837/835 claims, utilization review, and quality/HEDIS tracking.</Item>
          <Item label="Contact Center, SLA, Analytics, Interoperability, EDI">Member/provider engagement, penalty-backed SLA monitoring, reporting, FHIR interoperability, and EDI 834/835 transport.</Item>
          <Item label="Portals">Member and Provider self-service portals.</Item>
        </Section>

        <Section icon={Server} title="Tenant Control Plane">
          <p>Manage every MCO tenant in one place. Each card shows members, PMPM, estimated ARR, plan types, and modules. You can:</p>
          <Item label="Add a tenant">Create a new MCO (name, region, plan types, members, PMPM, status).</Item>
          <Item label="Change status">Move a tenant through Prospect → Discovery → Contracting → Implementation → Live.</Item>
          <Item label="Generate an explainer video">Produce a white-labeled product-tour video branded in the tenant's color, with an optional custom avatar and voiceover.</Item>
          <Item label="Switch tenant">Use the top-bar switcher to view the whole app as any tenant — the branding and data change to that MCO.</Item>
        </Section>

        <Section icon={Send} title="Submitting to Trillium">
          <p>Use the <strong>Trillium Submission Center</strong> (Business menu) as your single source for the submission:</p>
          <Item label="Submittal-Ready Packet">One ZIP with a submission checklist cover plus all submission-safe documents, correctly ordered.</Item>
          <Item label="Individual PDFs">Download any single document (RFP, Founder Credibility, Delivery Approach, AI-Augmented, Pricing Grid, Security).</Item>
          <Item label="Cover-email links">Copy the Live Demo and Showcase links to paste into your submission email.</Item>
          <Item label="Live Demo link">A public link that opens the real platform as a Care Manager — internal business tabs are hidden, so it is safe to send to Trillium.</Item>
        </Section>

        <Section icon={Rocket} title="Turnkey AI Go-To-Market">
          <p>Your master sales cockpit:</p>
          <Item label="Distribution & Submission">Quick access to the submittal packet, Showcase link, and Live Demo link.</Item>
          <Item label="Document Classification">Every asset labeled SHARE (safe for Trillium) or INTERNAL (never share) — each with a PDF download.</Item>
          <Item label="Duplicate for another MCO">One click generates a re-branded DRAFT packet for Alliance, Partners, or Vaya — so you can pursue other MCOs if Trillium doesn't land.</Item>
          <Item label="Prospect Leads">Warm leads captured from the public Showcase page.</Item>
          <Item label="Meeting Prep Cheat-Sheets">Talking points and a Q&A "war room" for intro, demo, security, and BAFO meetings.</Item>
        </Section>

        <Section icon={Globe} title="Public pages (share the concept)">
          <Item label="Showcase (/showcase)">A premium marketing page with explainer videos and a lead-capture form — share the concept with one link, no documents needed.</Item>
          <Item label="Live Demo (/demo)">Auto-opens the real product in a clean Care-Manager view for prospects.</Item>
          <p>Both are public (no login) — find and copy their links from the Go-To-Market or Submission Center pages.</p>
        </Section>

        <Section icon={Lock} title="Golden rule: Internal vs. Shareable">
          <p>Anything marked <strong>SHARE / external</strong> is sanitized and safe to send to Trillium. Anything marked <strong>INTERNAL</strong> (Delivery P&amp;L, AI-Operated Lean Model, Monetization, Sales/GTM playbooks) reveals your economics, margins, or strategy — <strong>never share these.</strong> The Submission Center and public packet only ever contain SHARE documents.</p>
        </Section>
      </div>
    </div>
  );
}
